var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Text, TouchableOpacity } from 'react-native';
import { Box, Select, IndexPath, luxColors, Icon, Iffy, useOrientationChange, useWindowSize, DNAText, InformationMessage, DNAButton, DNABox, } from '@alucio/lux-ui';
import { LocalVideoPreview, LocalAudioLevelIndicator, useVideoContext, useAppState, useAudioInputDevices, useAudioOutputDevices, useVideoInputDevices, useIsCameraAndMicAvailable, } from '../../main';
import { styles } from '../InputComponent/Styles';
const S = {
    Container: {
        flex: 1,
        flexDirection: 'column',
        alignSelf: 'flex-start',
        borderRadius: 4,
        borderWidth: 2,
        borderColor: luxColors.contentPanelBackground.tertiary,
        backgroundColor: luxColors.contentPanelBackground.primary,
        maxWidth: 560,
    },
    MobileContainer: {
        flex: 1,
        borderRadius: 4,
        borderWidth: 2,
        borderColor: luxColors.contentPanelBackground.tertiary,
        backgroundColor: luxColors.contentPanelBackground.primary,
    },
    MobileTitle: {
        Container: {
            flexDirection: 'row',
            borderBottomWidth: 1,
            borderStyle: 'solid',
            borderColor: luxColors.contentPanelBackground.tertiary,
            alignItems: 'center',
            justifyContent: 'space-between',
        },
        TextContainer: {
            marginLeft: 140,
            marginTop: 12,
            marginBottom: 12,
        },
        ButtonContainer: {
            marginBottom: 9,
            marginTop: 9,
            marginRight: 24,
        },
        MobileButton: {
            color: luxColors.thumbnailBorder.primary,
        },
        Text: {
            color: luxColors.info.primary,
            fontSize: 16,
            marginLeft: 16,
        },
    },
    Title: {
        Container: {
            paddingTop: 16,
            paddingBottom: 16,
            borderBottomWidth: 1,
            borderStyle: 'solid',
            borderColor: luxColors.contentPanelBackground.tertiary,
        },
        Text: {
            color: luxColors.info.primary,
            fontSize: 16,
            marginLeft: 16,
        },
    },
    Options: {
        Container: {
            padding: 24,
            alignItems: 'start',
        },
        Device: {
            flexDirection: 'row',
            alignItems: 'flex-end',
            marginBottom: 32,
            minWidth: 324,
        },
        Select: {
            paddingRight: 24,
            width: 324,
        },
        Icon: {
            width: 24,
            height: 24,
            color: luxColors.info.primary,
        },
        IconActive: {
            color: luxColors.mediaDevice.primary,
        },
        VideoPreview: {
            width: 112,
        },
    },
    Button: {
        Base: {
            backgroundColor: luxColors.thumbnailBorder.primary,
        },
        Round: {
            padding: 8,
            borderRadius: 200,
        },
        Confirm: {
            borderRadius: 200,
            alignSelf: 'flex-end',
            minHeight: 0,
            marginTop: 12,
            paddingTop: 8,
            paddingBottom: 8,
            paddingLeft: 12,
            paddingRight: 12,
        },
    },
};
const useAudioOutputTest = (audioFiles) => {
    const { activeSinkId } = useAppState();
    const [soundIdx, setSoundIdx] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const audioEl = useRef();
    const ignore = useRef(false);
    useEffect(() => () => {
        var _a, _b;
        (_a = audioEl.current) === null || _a === void 0 ? void 0 : _a.pause();
        (_b = audioEl.current) === null || _b === void 0 ? void 0 : _b.remove();
        ignore.current = true;
    }, []);
    const playAudio = useCallback(() => {
        var _a, _b;
        if (!isPlaying) {
            setIsPlaying(true);
            (_b = (_a = audioEl
                .current) === null || _a === void 0 ? void 0 : _a.play()) === null || _b === void 0 ? void 0 : _b.catch(err => console.warn(err));
        }
    }, [audioFiles, soundIdx, audioEl, isPlaying]);
    useEffect(() => {
        var _a;
        if (!audioEl.current) {
            const newAudioEl = new Audio(audioFiles[soundIdx]);
            newAudioEl.addEventListener('ended', () => {
                if (!ignore.current) {
                    setSoundIdx(p => p < audioFiles.length - 1 ? p + 1 : 0);
                    setIsPlaying(false);
                }
            });
            (_a = newAudioEl.setSinkId) === null || _a === void 0 ? void 0 : _a.call(newAudioEl, activeSinkId);
            document.body.appendChild(newAudioEl);
            audioEl.current = newAudioEl;
        }
        else {
            audioEl.current.src = audioFiles[soundIdx];
        }
    }, [audioFiles, soundIdx]);
    useEffect(() => {
        var _a, _b;
        (_b = (_a = audioEl.current) === null || _a === void 0 ? void 0 : _a.setSinkId) === null || _b === void 0 ? void 0 : _b.call(_a, activeSinkId);
    }, [activeSinkId]);
    return { playAudio, isPlaying };
};
const useDeviceSelect = (deviceType) => {
    var _a;
    const { room: { localParticipant }, localTracks, getLocalAudioTrack, getLocalVideoTrack, videoDeviceOptions, setVideoDeviceOptions, } = useVideoContext();
    const { activeSinkId, setActiveSinkId } = useAppState();
    const audioOutputDevices = useAudioOutputDevices();
    const audioInputDevices = useAudioInputDevices();
    const videoInputDevices = useVideoInputDevices();
    const [idx, setIdx] = useState(new IndexPath(-1));
    const [deviceAborted, setDeviceAborted] = useState();
    const devices = {
        audioinput: audioInputDevices,
        audiooutput: audioOutputDevices,
        videoinput: videoInputDevices,
    }[deviceType];
    const trackType = deviceType === 'audioinput' ? 'audio' : 'video';
    const localTrack = localTracks.find(track => (track.kind === trackType));
    const localTrackLabel = (_a = localTrack === null || localTrack === void 0 ? void 0 : localTrack.mediaStreamTrack) === null || _a === void 0 ? void 0 : _a.label;
    const isAudioOutput = deviceType === 'audiooutput';
    const getLocalTrackByType = {
        audio: (newDeviceId) => getLocalAudioTrack(newDeviceId),
        video: (newDeviceId) => getLocalVideoTrack({ deviceId: { exact: newDeviceId } }),
    };
    function unpublishTrack() {
        localTrack === null || localTrack === void 0 ? void 0 : localTrack.stop();
        if (localTrack) {
            const localTrackPublication = localParticipant === null || localParticipant === void 0 ? void 0 : localParticipant.unpublishTrack(localTrack);
            localParticipant === null || localParticipant === void 0 ? void 0 : localParticipant.emit('trackUnpublished', localTrackPublication);
        }
    }
    function replaceTrack(newDevice) {
        return __awaiter(this, void 0, void 0, function* () {
            const isPreviousAudioInputDisabled = deviceType === 'audioinput' && !(localTrack === null || localTrack === void 0 ? void 0 : localTrack.isEnabled);
            unpublishTrack();
            const newTrack = yield getLocalTrackByType[trackType](newDevice.deviceId);
            // If previous audio track/device was muted, mute the next device as well
            if (isPreviousAudioInputDisabled) {
                newTrack.disable();
            }
            localParticipant === null || localParticipant === void 0 ? void 0 : localParticipant.publishTrack(newTrack);
        });
    }
    // This useEffect runs every time when a device is connected or disconnected
    // to check the status of the current device, also sets preferred device
    // from previous meetings if it's available
    useEffect(() => {
        var _a;
        // Checks preferred device availability
        const preferredDeviceId = localStorage.getItem(`beacon-default-${deviceType}`);
        if (preferredDeviceId) {
            const preferredDeviceIdx = devices.findIndex(device => device.deviceId === preferredDeviceId);
            if (preferredDeviceIdx >= 0) {
                setIdx(new IndexPath(preferredDeviceIdx));
                return;
            }
        }
        // Check current device availability, otherwise fallback to default system device
        const localTrackDeviceIdx = devices.findIndex(device => {
            return isAudioOutput ? device.deviceId === activeSinkId : device.label === localTrackLabel;
        });
        if (localTrackDeviceIdx >= 0) {
            setIdx(new IndexPath(localTrackDeviceIdx));
        }
        else {
            const isDeviceUnset = idx.row === 0;
            const isAbortedDevice = !isDeviceUnset;
            const isUnavailableDevices = isDeviceUnset && !devices.length;
            // When device is disconnected unpublish the track and fallback to system default device
            if (isAbortedDevice || isUnavailableDevices) {
                if (isAbortedDevice && !isAudioOutput) {
                    unpublishTrack();
                    setDeviceAborted(localTrackLabel);
                }
                setIdx(new IndexPath(0));
            }
            // "Disabling" video actually unpublishes the track (whereas audio just changes the .isEnabled property)
            // By default the example app has a usecase for re-enabling the same video, so we leverage that existing state (videoDeviceOptions)
            if (deviceType === 'videoinput' && devices.length) {
                // @ts-ignore [twillio has a bad TS type on deviceId.exact]
                const videoDeviceId = (_a = videoDeviceOptions === null || videoDeviceOptions === void 0 ? void 0 : videoDeviceOptions.deviceId) === null || _a === void 0 ? void 0 : _a.exact;
                const videoDeviceIdx = devices.findIndex(device => {
                    return device.deviceId === videoDeviceId || device.label === videoDeviceId;
                });
                setIdx(new IndexPath(videoDeviceIdx));
            }
        }
    }, [devices.length, localTracks.length]);
    function onDeviceSelected(index) {
        const nextDevice = devices === null || devices === void 0 ? void 0 : devices[index.row];
        if (nextDevice) {
            // Sets preferred device in localStorage
            localStorage.setItem(`beacon-default-${deviceType}`, nextDevice.deviceId);
        }
        setIdx(index);
    }
    // Replace track on select list change (via user input or via above useEffect)
    useEffect(() => {
        const nextDevice = devices === null || devices === void 0 ? void 0 : devices[idx.row];
        const nextDeviceLabel = nextDevice === null || nextDevice === void 0 ? void 0 : nextDevice.label;
        const isDifferentDevice = (localTrackLabel && nextDeviceLabel) && (localTrackLabel !== nextDeviceLabel);
        const isResumingAbortedDevice = (deviceAborted && nextDeviceLabel) && (deviceAborted === nextDeviceLabel);
        const isVideoDeviceDisabled = !(localTrack === null || localTrack === void 0 ? void 0 : localTrack.isEnabled) && nextDevice && deviceType === 'videoinput';
        if (!nextDevice)
            return;
        if (isAudioOutput) {
            setActiveSinkId(nextDevice.deviceId);
            return;
        }
        if (isDifferentDevice || isResumingAbortedDevice) {
            replaceTrack(nextDevice);
            setDeviceAborted(undefined);
            return;
        }
        // Modify top level "video device resume" state
        if (isVideoDeviceDisabled) {
            setVideoDeviceOptions({ deviceId: { exact: nextDevice.deviceId } });
        }
    }, [idx]);
    return {
        idx,
        setIdx: (index) => {
            // Circumvents TS fn signature
            if (!Array.isArray(index))
                onDeviceSelected(index);
        },
        devices,
        device: devices === null || devices === void 0 ? void 0 : devices[idx.row],
    };
};
export const DeviceSelect = (props) => {
    const { label, deviceType, children, customStyles, basicSelectStyle } = props;
    const { idx, setIdx, devices, device } = useDeviceSelect(deviceType);
    const orientation = useOrientationChange();
    function getDeviceLabel(device) {
        if (device) {
            return device.deviceId === 'default' ? 'Same as System' : device.label;
        }
        // Graceful label fallback for most browsers
        // (i.e. no support for audio output device selection)
        return deviceType === 'audiooutput' ? 'System Default Audio Output' : ' ';
    }
    return (React.createElement(Box, { style: [S.Options.Device, orientation === 'LANDSCAPE' && { marginBottom: 12 }, customStyles] },
        React.createElement(Select, { value: getDeviceLabel(device), label: label, selectedIndex: idx, onSelect: setIdx, style: [S.Options.Select, customStyles], status: basicSelectStyle ? 'basic' : 'dark' }, devices.map((device) => (React.createElement(Select.Item, { key: device.deviceId, title: getDeviceLabel(device) })))),
        children));
};
export var Status;
(function (Status) {
    Status[Status["primary"] = 0] = "primary";
    Status[Status["warning"] = 1] = "warning";
})(Status || (Status = {}));
export const AudioTestButton = (props) => {
    const { audioFiles, customStyles, status } = props;
    const { playAudio, isPlaying } = useAudioOutputTest(audioFiles);
    return (React.createElement(React.Fragment, null,
        React.createElement(Iffy, { is: status === Status.primary },
            React.createElement(Box, null,
                React.createElement(TouchableOpacity, { style: [S.Button.Base, S.Button.Round, customStyles], onPress: playAudio, activeOpacity: 1 },
                    React.createElement(Icon, { name: `volume-${isPlaying ? 'high' : 'medium'}`, style: [S.Options.Icon] })))),
        React.createElement(Iffy, { is: status === Status.warning },
            React.createElement(DNAButton, { status: "warning", iconLeft: `volume-${isPlaying ? 'high' : 'medium'}`, onPress: playAudio, style: customStyles, size: isPlaying ? 'tiny' : 'small' }, isPlaying ? 'Playing' : 'Test'))));
};
const MobileDeviceSettings = (props) => {
    const { onDone, disableLocalAudio } = props;
    const size = useWindowSize();
    const { disabledMessage, hasMicrophonePermissions, hasWebcamPermissions, camAvailable, micAvailable, } = useIsCameraAndMicAvailable();
    return (React.createElement(Box, { style: [S.MobileContainer, { height: size.height }] },
        React.createElement(Box, { style: S.MobileTitle.Container },
            React.createElement(Box, { style: [S.MobileTitle.TextContainer, { alignSelf: 'center' }] },
                React.createElement(Text, { style: [S.MobileTitle.Text, { alignSelf: 'center' }] }, "Settings")),
            React.createElement(Iffy, { is: disabledMessage },
                React.createElement(DNAText, { style: styles.DisabledMessage }, disabledMessage)),
            React.createElement(Box, { style: S.MobileTitle.ButtonContainer },
                React.createElement(TouchableOpacity, { activeOpacity: 1, onPress: onDone },
                    React.createElement(Text, { style: { color: luxColors.thumbnailBorder.primary, fontSize: 18 } }, "Done")))),
        React.createElement(Box, { style: [S.Options.Container, { alignItems: 'center' }] },
            React.createElement(Iffy, { is: !disableLocalAudio },
                (hasMicrophonePermissions && micAvailable) && React.createElement(DeviceSelect, { label: "MICROPHONE", deviceType: "audioinput" }),
                React.createElement(DeviceSelect, { label: "SPEAKERS", deviceType: "audiooutput" })),
            (hasWebcamPermissions && camAvailable) && React.createElement(DeviceSelect, { label: "VIDEO", deviceType: "videoinput" }))));
};
const DefaultDeviceSettings = (props) => {
    const { onDone, audioFiles, disableLocalAudio } = props;
    const { disabledMessage, hasMicrophonePermissions, hasWebcamPermissions, camAvailable, micAvailable, } = useIsCameraAndMicAvailable();
    return (React.createElement(Box, { style: [S.Container] },
        React.createElement(Box, { style: [S.Title.Container] },
            React.createElement(Text, { style: [S.Title.Text] }, "Settings")),
        React.createElement(Box, { style: [S.Options.Container] },
            React.createElement(Iffy, { is: disabledMessage },
                React.createElement(DNABox, { style: { minWidth: 270, marginVertical: 8 } },
                    React.createElement(InformationMessage, { variance: "darkWarning", text: disabledMessage || '' }))),
            React.createElement(Iffy, { is: !disableLocalAudio },
                (hasMicrophonePermissions && micAvailable) && React.createElement(DeviceSelect, { label: "MICROPHONE", deviceType: "audioinput" },
                    React.createElement(Box, { style: { padding: 6 } },
                        React.createElement(LocalAudioLevelIndicator, { size: 23 }))),
                React.createElement(DeviceSelect, { label: "SPEAKERS", deviceType: "audiooutput" },
                    React.createElement(AudioTestButton, { audioFiles: audioFiles }))),
            (hasWebcamPermissions && camAvailable) && React.createElement(DeviceSelect, { label: "VIDEO", deviceType: "videoinput" },
                React.createElement(Box, { style: S.Options.VideoPreview },
                    React.createElement(LocalVideoPreview, { placeholderIconStyle: { height: 48, marginTop: -20 } }))),
            React.createElement(TouchableOpacity, { activeOpacity: 1, style: [S.Button.Base, S.Button.Confirm], onPress: onDone },
                React.createElement(Text, { style: { color: 'white' } }, "Done")))));
};
const DeviceSettings = (props) => {
    const { onDone, audioFiles, disableLocalAudio, mobileView } = props;
    return (React.createElement(React.Fragment, null,
        React.createElement(Iffy, { is: !mobileView },
            React.createElement(DefaultDeviceSettings, { onDone: onDone, audioFiles: audioFiles, disableLocalAudio: disableLocalAudio })),
        React.createElement(Iffy, { is: mobileView },
            React.createElement(MobileDeviceSettings, { onDone: onDone, disableLocalAudio: disableLocalAudio }))));
};
export default DeviceSettings;
//# sourceMappingURL=DeviceSettings.js.map